import { Controller } from "@hotwired/stimulus";
import { slotLabels, normalizeTime, ALLOWED_TIME_CHARS } from "./helpers/time_combobox";

// A combobox time picker: a text input the user can type into freely, plus a
// dropdown of 30-minute slots (12:00 AM … 11:30 PM) to pick from. Clicking /
// focusing the input opens the dropdown; typing filters/keeps it open. On blur
// the typed value is normalized to "H:MM AM/PM". Submits a 12-hour string the
// server parses with Time.parse.
//
//   <div data-controller="time-select">
//     <input data-time-select-target="input" ...>
//     <ul data-time-select-target="menu"></ul>
//   </div>
export default class extends Controller {
  static targets = ["input", "menu"];
  // Day-boundary variants that avoid the ambiguous "Midnight" slot:
  //   endOfDay   — drop Midnight, end at 11:59 PM (a single-day event ends the
  //                same day instead of rolling into the next).
  //   startOfDay — drop Midnight, start at 12:01 AM (the day's first time, with
  //                no "which midnight?" ambiguity).
  static values = { endOfDay: Boolean, startOfDay: Boolean };

  // Onboarding uses the word labels "Midnight"/"Noon".
  get labelOpts() {
    return { words: true, startOfDay: this.startOfDayValue, endOfDay: this.endOfDayValue };
  }

  connect() {
    this._onDocClick = this.onDocumentClick.bind(this);
    document.addEventListener("click", this._onDocClick);
    this.buildOptions();

    // Bind the input's own events here (rather than data-action on each input)
    // so every time-select works the same without per-field markup.
    this._onFocus = () => this.open();
    this._onClick = () => this.open();
    this._onInput = () => { this.open(); this.highlightMatch(); };
    this._onBlur = () => this.onBlur();
    this._onKeydown = (e) => this.filterKeydown(e);
    this._onBeforeInput = (e) => this.filterBeforeInput(e);
    this.inputTarget.addEventListener("focus", this._onFocus);
    this.inputTarget.addEventListener("click", this._onClick);
    this.inputTarget.addEventListener("input", this._onInput);
    this.inputTarget.addEventListener("blur", this._onBlur);
    this.inputTarget.addEventListener("keydown", this._onKeydown);
    this.inputTarget.addEventListener("beforeinput", this._onBeforeInput);
  }

  disconnect() {
    document.removeEventListener("click", this._onDocClick);
  }

  filterBeforeInput(e) {
    if (e.data && !ALLOWED_TIME_CHARS.test(e.data)) e.preventDefault();
  }

  filterKeydown(e) {
    // Allow control/navigation keys and shortcuts.
    if (e.ctrlKey || e.metaKey || e.altKey) return;
    const allowedKeys = ["Backspace", "Delete", "Tab", "Enter", "Escape",
                         "ArrowLeft", "ArrowRight", "ArrowUp", "ArrowDown", "Home", "End"];
    if (allowedKeys.includes(e.key)) return;
    if (e.key.length === 1 && !ALLOWED_TIME_CHARS.test(e.key)) e.preventDefault();
  }

  buildOptions() {
    const frag = document.createDocumentFragment();
    for (const label of slotLabels(this.labelOpts)) {
      const li = document.createElement("li");
      li.className = "time-select-option";
      li.textContent = label;
      li.dataset.value = label;
      li.setAttribute("role", "option");
      li.addEventListener("mousedown", (e) => {
        // mousedown (not click) so it fires before the input's blur.
        e.preventDefault();
        this.select(label);
      });
      frag.appendChild(li);
    }
    this.menuTarget.replaceChildren(frag);
  }

  open() {
    this.menuTarget.classList.add("is-open");
    this.scrollToSelected();
  }

  close() {
    this.menuTarget.classList.remove("is-open");
  }

  select(label) {
    this.inputTarget.value = label;
    this.close();
    // Notify listeners (e.g. the onboarding end-time constraint).
    this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  // Normalize a freely-typed value to "H:MM AM/PM" on blur; clear if unparseable.
  onBlur() {
    setTimeout(() => this.close(), 120); // allow option mousedown to land first
    const normalized = normalizeTime(this.inputTarget.value, this.labelOpts);
    this.inputTarget.value = normalized || "";
    if (normalized) this.inputTarget.dispatchEvent(new Event("change", { bubbles: true }));
  }

  highlightMatch() {
    const val = this.inputTarget.value.trim().toLowerCase();
    let match = null;
    this.menuTarget.querySelectorAll(".time-select-option").forEach((li) => {
      const on = val && li.dataset.value.toLowerCase().startsWith(val);
      li.classList.toggle("is-active", !!on);
      if (on && !match) match = li;
    });
    if (match) match.scrollIntoView({ block: "nearest" });
  }

  scrollToSelected() {
    const val = this.inputTarget.value.trim();
    const sel = [...this.menuTarget.querySelectorAll(".time-select-option")]
      .find((li) => li.dataset.value === val);
    if (sel) sel.scrollIntoView({ block: "center" });
  }

  onDocumentClick(e) {
    if (!this.element.contains(e.target)) this.close();
  }
}
